/* HOMA header behaviour: mega menu, drawer, search, locale, Ajax cart.
 *
 * Loaded as a module by sections/homa-full-header.liquid. Everything is
 * delegated off the document so section re-renders in the theme editor do not
 * need re-binding.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

const q = (sel, root = document) => root.querySelector(sel);
const qa = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ── Scroll state ───────────────────────────────────────────── */

function initScrollState() {
  const nav = q('[data-hf-nav]');
  if (!nav) return;

  let ticking = false;
  const update = () => {
    nav.classList.toggle('is-scrolled', window.scrollY > 50);
    ticking = false;
  };

  update();
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );
}

/* ── Mega menu ──────────────────────────────────────────────── */

function initMega() {
  const nav = q('[data-hf-nav]');
  const wrap = q('[data-hf-products]');
  const btn = q('[data-hf-products-btn]');
  const mega = q('[data-hf-mega]');
  if (!nav || !wrap || !btn || !mega) return;

  let closeTimer = null;

  const open = () => {
    clearTimeout(closeTimer);
    mega.hidden = false;
    wrap.classList.add('is-open');
    nav.classList.add('is-active');
    btn.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    mega.hidden = true;
    wrap.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    if (!q('[data-hf-bag-panel]:not([hidden])')) nav.classList.remove('is-active');
  };

  // The 180ms grace period matches the design and lets the pointer cross the gap
  // between the trigger and the panel.
  const scheduleClose = () => {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, 180);
  };

  btn.addEventListener('click', () => (mega.hidden ? open() : close()));
  wrap.addEventListener('mouseenter', open);
  wrap.addEventListener('mouseleave', scheduleClose);
  mega.addEventListener('mouseenter', () => clearTimeout(closeTimer));
  mega.addEventListener('mouseleave', scheduleClose);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !mega.hidden) {
      close();
      btn.focus();
    }
  });

  document.addEventListener('click', (e) => {
    if (mega.hidden) return;
    if (!wrap.contains(e.target) && !mega.contains(e.target)) close();
  });

  // Series tabs
  qa('[data-hf-series-btn]', mega).forEach((tab) => {
    const activate = () => {
      const id = tab.dataset.hfSeriesBtn;
      qa('[data-hf-series-btn]', mega).forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      qa('[data-hf-series-panel]', mega).forEach((p) => {
        p.hidden = p.dataset.hfSeriesPanel !== id;
      });
    };
    tab.addEventListener('mouseenter', activate);
    tab.addEventListener('focus', activate);
    tab.addEventListener('click', activate);
  });
}

/* ── Nav hover frosting ─────────────────────────────────────── */

function initHoverState() {
  const nav = q('[data-hf-nav]');
  if (!nav || !nav.hasAttribute('data-hf-home')) return;

  nav.addEventListener('mouseenter', () => nav.classList.add('is-active'));
  nav.addEventListener('mouseleave', () => {
    const panelOpen =
      !q('[data-hf-mega]')?.hidden || !q('[data-hf-bag-panel]')?.hidden;
    if (!panelOpen && !nav.classList.contains('is-scrolled')) {
      nav.classList.remove('is-active');
    }
  });
}

/* ── Generic overlay (drawer + search) ──────────────────────── */

function initOverlay({ root, openSel, closeSel, focusSel }) {
  const el = q(root);
  if (!el) return;

  let lastFocused = null;

  const open = () => {
    lastFocused = document.activeElement;
    el.hidden = false;
    document.body.style.overflow = 'hidden';
    qa(openSel).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const target = focusSel ? q(focusSel, el) : q('button, a, input', el);
    target?.focus();
  };

  const close = () => {
    el.hidden = true;
    document.body.style.overflow = '';
    qa(openSel).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    lastFocused?.focus();
  };

  qa(openSel).forEach((b) => b.addEventListener('click', open));
  qa(closeSel, el).forEach((b) => b.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !el.hidden) close();
  });

  // Keep tab focus inside the panel while it is open.
  el.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = qa(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      el
    ).filter((n) => n.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
}

/* ── Popovers (bag, locale) ─────────────────────────────────── */

function initPopover(wrapSel, toggleSel, panelSel) {
  const wrap = q(wrapSel);
  const toggle = q(toggleSel);
  const panel = q(panelSel);
  if (!wrap || !toggle || !panel) return;

  const nav = q('[data-hf-nav]');

  const close = () => {
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (nav && q('[data-hf-mega]')?.hidden !== false) nav.classList.remove('is-active');
  };

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', String(willOpen));
    if (willOpen && nav) nav.classList.add('is-active');
  });

  document.addEventListener('click', (e) => {
    if (!panel.hidden && !wrap.contains(e.target)) close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden) {
      close();
      toggle.focus();
    }
  });
}

/* ── Region banner ──────────────────────────────────────────── */

function initRegionBanner() {
  const banner = q('[data-hf-region]');
  if (!banner) return;

  const key = `hf-region-dismissed:${banner.dataset.hfRegionKey || 'default'}`;
  let dismissed = false;
  try {
    dismissed = localStorage.getItem(key) === '1';
  } catch {
    // Private mode; just show the banner.
  }

  if (!dismissed) banner.hidden = false;

  q('[data-hf-region-close]', banner)?.addEventListener('click', () => {
    banner.hidden = true;
    try {
      localStorage.setItem(key, '1');
    } catch {
      // Nothing to persist to; dismissal lasts for the session only.
    }
  });
}

/* ── Locale form ────────────────────────────────────────────── */

function initLocale() {
  initPopover('[data-hf-locale]', '[data-hf-locale-toggle]', '[data-hf-locale-panel]');
}

/* ── Ajax cart ──────────────────────────────────────────────── */

async function refreshCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=homa-cart-drawer`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!res.ok) return;

  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const contents = q('[data-hf-cart-contents]');
  const fresh = q('[data-hf-cart-contents]', doc);
  if (contents && fresh) contents.innerHTML = fresh.innerHTML;

  const countEl = q('[data-hf-cart-count]');
  const freshCount = q('[data-hf-cart-count]', doc);
  if (countEl && freshCount) {
    countEl.textContent = freshCount.textContent;
    countEl.hidden = freshCount.hidden;
  }
}

function initAjaxCart() {
  // Any form posting to /cart/add is intercepted so the page never reloads.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action.includes('/cart/add')) return;

    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('disabled', '');

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!res.ok) {
        // Fall back to a normal navigation so the customer still sees the error.
        form.submit();
        return;
      }

      await refreshCart();

      const panel = q('[data-hf-bag-panel]');
      const toggle = q('[data-hf-bag-toggle]');
      if (panel && toggle) {
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        q('[data-hf-nav]')?.classList.add('is-active');
      }
    } catch {
      form.submit();
    } finally {
      submit?.removeAttribute('disabled');
    }
  });

  // Other scripts on the page can ask the header to resync.
  document.addEventListener('hf:cart:refresh', refreshCart);
}

function initHeaderMetrics() {
  const group = document.querySelector('#header-group');
  if (!group) return;

  const sync = () => {
    const h = Math.round(group.getBoundingClientRect().height);
    document.body.style.setProperty('--header-group-height', `${h}px`);
  };

  sync();
  window.addEventListener('resize', sync, { passive: true });

  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(sync);
    ro.observe(group);
  }
}

/* ── Boot ───────────────────────────────────────────────────── */

function init() {
  initHeaderMetrics();
  initScrollState();
  initHoverState();
  initMega();
  initOverlay({
    root: '[data-hf-drawer]',
    openSel: '[data-hf-drawer-open]',
    closeSel: '[data-hf-drawer-close]',
  });
  initOverlay({
    root: '[data-hf-search]',
    openSel: '[data-hf-search-open]',
    closeSel: '[data-hf-search-close]',
    focusSel: '[data-hf-search-input]',
  });
  initPopover('[data-hf-bag]', '[data-hf-bag-toggle]', '[data-hf-bag-panel]');
  initLocale();
  initRegionBanner();
  initAjaxCart();

  if (REDUCED.matches) document.documentElement.classList.add('hf-reduced-motion');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// The theme editor tears down and re-renders sections.
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('[data-hf-nav]')) init();
});
